import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { readContract, writeContract } from '@wagmi/core';
import postConsensusContractABI from '../../contracts/tgetherPostConsensus.json';
import communityConsensusContractABI from '../../contracts/tgetherCommunityConsensus.json';
import * as config from '../../config.tsx';

const postConsensusContractAddress = process.env.REACT_APP_POSTS_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;
const communityConsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;

interface ReviewSubmissionModalProps {
  show: boolean;
  handleClose: () => void;
  communitySubmissionId: string;
  communityName: string;
  onSubmissionSuccess: () => void;
}

function ReviewSubmissionModal({
  show,
  handleClose,
  communitySubmissionId,
  communityName,
  onSubmissionSuccess = () => {},
}: ReviewSubmissionModalProps) {
  const [consensusTypes, setConsensusTypes] = useState<string[]>(['Accept', 'Reject']);
  const [selectedConsensus, setSelectedConsensus] = useState<string>('');
  const [reviewContent, setReviewContent] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loadingConsensusTypes, setLoadingConsensusTypes] = useState(false);
  const [extraConsensusTypesLoaded, setExtraConsensusTypesLoaded] = useState(false);

  useEffect(() => {
    if (!show) {
      // Reset form state when modal is closed
      setSelectedConsensus('');
      setReviewContent('');
      setError('');
      setInfoMessage('');
      setExtraConsensusTypesLoaded(false);
    }
  }, [show]);

  const navigate = useNavigate();

  const fetchAdditionalConsensusTypes = async () => {
    setLoadingConsensusTypes(true);
    setError('');
    setInfoMessage('');

    try {
      const types = await readContract(config.config, {
        abi: communityConsensusContractABI.abi,
        address: communityConsensusContractAddress,
        functionName: 'getCommunityConsensousTypes',
        args: [communityName],
      });

      setConsensusTypes((prev) => [...prev, ...(types as string[])]);
      setExtraConsensusTypesLoaded(true);
      setInfoMessage('Additional consensus types loaded successfully.');
    } catch (error) {
      console.error('Error fetching consensus types:', error);
      setError('Failed to fetch additional consensus types.');
    } finally {
      setLoadingConsensusTypes(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!selectedConsensus) {
      setError('Please select a valid consensus type.');
      return;
    }

    if (!reviewContent.trim()) {
      setError('Review content cannot be empty.');
      return;
    }

    try {
      let consensusIndex: number;

      if (selectedConsensus === 'Accept') {
        consensusIndex = 2;
      } else if (selectedConsensus === 'Reject') {
        consensusIndex = 3;
      } else {
        // Additional consensus types start from index 4
        const additionalIndex = consensusTypes.indexOf(selectedConsensus);
        if (additionalIndex >= 2) {
          consensusIndex = additionalIndex + 2; // Adjust by 2 to align with the contract logic
        } else {
          setError('Please select a valid consensus type.');
          return;
        }
      }

      const tx = await writeContract(config.config, {
        abi: postConsensusContractABI.abi,
        address: postConsensusContractAddress,
        functionName: 'submitReview',
        args: [communitySubmissionId, reviewContent, consensusIndex],
      });

      onSubmissionSuccess();
      navigate(`/search-results?type=Review&value=${encodeURIComponent(communitySubmissionId)}`);
      handleClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Submit a Review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {infoMessage && <Alert variant="info">{infoMessage}</Alert>}
        <Form>
          <Form.Group controlId="formConsensusType">
            <Form.Label>Consensus Type</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                as="select"
                value={selectedConsensus}
                onChange={(e) => {
                  setSelectedConsensus(e.target.value);
                  setError(''); // Clear error when user changes the selection
                }}
              >
                <option value="">Select Consensus Type</option>
                {consensusTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Control>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>Load more consensus options from the community.</Tooltip>}
              >
                <Button
                  variant="info"
                  onClick={fetchAdditionalConsensusTypes}
                  className="ml-2"
                  disabled={extraConsensusTypesLoaded || loadingConsensusTypes}
                >
                  {loadingConsensusTypes ? 'Loading...' : 'Load More'}
                </Button>
              </OverlayTrigger>
            </div>
          </Form.Group>
          <Form.Group controlId="formReviewContent">
            <Form.Label>Review Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reviewContent}
              onChange={(e) => {
                setReviewContent(e.target.value);
                setError(''); // Clear error when user starts typing
              }}
            />
          </Form.Group>
          <Button variant="primary" onClick={handleSubmit}>
            Submit Review
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default ReviewSubmissionModal;
